/* ============================================================
   Background Video 310 — Carousel controller
   Vanilla JS. Handles arrows, dots, swipe, video pause-on-change,
   the optional play/pause + sound controls, and lazy-loading iframes
   (YouTube/Vimeo) on first activation.
   ============================================================ */

(function () {
  'use strict';

  class HeroCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-carousel-track]');
      this.slides = Array.from(root.querySelectorAll('[data-carousel-slide]'));
      this.prevBtn = root.querySelector('[data-carousel-prev]');
      this.nextBtn = root.querySelector('[data-carousel-next]');
      this.mediaControls = root.querySelector('[data-carousel-media-controls]');
      this.playPauseBtn = root.querySelector('[data-carousel-playpause]');
      this.statusRegion = root.querySelector('[data-carousel-status]');
      this.muteBtn = root.querySelector('[data-carousel-mute]');
      this.count = this.slides.length;
      this.buildDots();
      this.dots = Array.from(root.querySelectorAll('[data-carousel-dot]'));

      this.index = 0;
      this.loop = root.dataset.loop === 'true';

      // Visitor-driven media state (set before bind()/activate() so both can honour it).
      this.userPaused = false; // Pause pressed → hold BOTH the video and the auto-advance
      this.soundOn = false;    // videos ship muted (browsers require it to autoplay)

      this.dragging = false;
      this.dragStartX = 0;
      this.dragDx = 0;

      if (this.count <= 1) {
        if (this.prevBtn) this.prevBtn.hidden = true;
        if (this.nextBtn) this.nextBtn.hidden = true;
      }

      this.bind();
      this.activate(0, { skipAnimation: true });
      this.root.__heroCarouselInstance = this;
      this.initAutoplay();
    }

    buildDots() {
      var container = this.root.querySelector('[data-carousel-dots]');
      if (!container) return;
      if (this.count <= 1) { container.style.display = 'none'; this.root.classList.add('hero-carousel--no-pager'); return; } // single slide → no pager
      container.style.display = '';
      this.root.classList.remove('hero-carousel--no-pager');
      if (container.querySelector('[data-carousel-dot]')) return; // already rendered in Liquid
      for (var i = 0; i < this.count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'hero-carousel__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('data-carousel-dot', i);
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        var fill = document.createElement('span');
        fill.className = 'hero-carousel__dot-fill';
        fill.setAttribute('data-dot-fill', '');
        dot.appendChild(fill);
        container.appendChild(dot);
      }
    }

    initAutoplay() {
      this.autoplay = this.root.dataset.autoplay === 'true' && this.count > 1;
      this.interval = parseInt(this.root.dataset.interval, 10) || 6000;
      this.elapsed = 0;
      this.lastTs = null;
      this.rafId = null;
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.boundTick = this.tick.bind(this);

      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!this.autoplay || reduce) {
        this.autoplay = false;
        this.setProgress(1); // no auto-advance → show the active pill filled/solid
        this.syncMediaControls();
        return;
      }

      this.root.addEventListener('mouseenter', () => { this.hovered = true; this.pauseAutoplay(); });
      this.root.addEventListener('mouseleave', () => { this.hovered = false; this.resumeAutoplay(); });
      this.root.addEventListener('focusin',  () => { this.focused = true; this.pauseAutoplay(); });
      this.root.addEventListener('focusout', () => { this.focused = false; this.resumeAutoplay(); });
      this.track.addEventListener('pointerdown', () => this.pauseAutoplay()); // touch swipe → resume on pointerup via go()
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) { this.pauseAutoplay(); } else { this.resumeAutoplay(); }
      });

      this.syncMediaControls();
      this.startLoop();
    }

    startLoop() {
      if (!this.autoplay) return;
      this.cancelLoop();
      this.lastTs = null; // reset so a pause gap never counts as elapsed time
      this.rafId = requestAnimationFrame(this.boundTick);
    }

    cancelLoop() {
      if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null; }
    }

    tick(ts) {
      if (this.paused || !this.autoplay) return;
      if (this.lastTs === null) this.lastTs = ts;
      this.elapsed += ts - this.lastTs;
      this.lastTs = ts;

      if (this.elapsed >= this.interval) {
        var next = this.nextIndex();
        this.elapsed = 0;
        if (next === this.index) { // non-loop carousel reached the last slide
          this.setProgress(1);
          this.cancelLoop();
          return;
        }
        this.setProgress(0);
        this.activate(next);
      } else {
        this.setProgress(this.elapsed / this.interval);
      }
      this.rafId = requestAnimationFrame(this.boundTick);
    }

    nextIndex() {
      var t = this.index + 1;
      if (this.loop) return ((t % this.count) + this.count) % this.count;
      return Math.min(this.count - 1, t);
    }

    pauseAutoplay() {
      if (!this.autoplay || this.paused) return;
      this.paused = true;
      this.cancelLoop();
    }

    resumeAutoplay() {
      if (!this.autoplay || !this.paused) return;
      if (this.userPaused || this.hovered || this.focused || document.hidden) return; // something still holds it paused
      this.paused = false;
      this.startLoop(); // elapsed preserved → bar resumes from where it froze
    }

    // Width of the white fill on each pager indicator (active = progress, others = 0)
    setProgress(p) {
      if (!this.dots || !this.dots.length) return;
      var clamped = Math.max(0, Math.min(1, p));
      this.dots.forEach((dot, idx) => {
        var fill = dot.querySelector('[data-dot-fill]');
        if (!fill) return;
        fill.style.width = (idx === this.index ? clamped * 100 : 0) + '%';
      });
    }

    bind() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.index + 1));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const i = parseInt(dot.dataset.carouselDot, 10);
          if (!Number.isNaN(i)) this.go(i);
        });
      });

      if (this.playPauseBtn) this.playPauseBtn.addEventListener('click', () => this.togglePlayPause());
      if (this.muteBtn) this.muteBtn.addEventListener('click', () => this.toggleSound());

      // A slide can carry both a desktop and a mobile video (one is display:none at a time),
      // so re-target the sound when the breakpoint flips under the visitor.
      if (this.muteBtn && window.matchMedia) {
        const mq = window.matchMedia('(max-width: 768px)');
        const onBreakpoint = () => { this.applySound(); this.syncMediaControls(); };
        if (mq.addEventListener) { mq.addEventListener('change', onBreakpoint); }
        else if (mq.addListener) { mq.addListener(onBreakpoint); }
      }

      // The media box is re-measured on any reflow (rotation, address-bar resize, …)
      if (this.mediaControls) {
        const onResize = () => {
          if (this.positionRaf) cancelAnimationFrame(this.positionRaf);
          this.positionRaf = requestAnimationFrame(() => this.positionMediaControls());
        };
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
      }

      // Keyboard
      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  { this.go(this.index - 1); }
        if (e.key === 'ArrowRight') { this.go(this.index + 1); }
      });

      // Touch / pointer swipe
      this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.track.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.track.addEventListener('pointerup',   this.onPointerUp.bind(this));
      this.track.addEventListener('pointercancel', this.onPointerUp.bind(this));
      this.track.addEventListener('dragstart', (e) => e.preventDefault());
    }

    onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      // Record the start, but do NOT capture the pointer or mark dragging yet.
      // Capturing on every pointerdown swallows clicks on links/buttons inside a
      // slide (the click gets retargeted to the track) — so taps must pass through.
      this.pointerActive = true;
      this.dragging = false;
      this.dragStartX = e.clientX;
      this.dragDx = 0;
      this.activePointerId = e.pointerId;
    }

    onPointerMove(e) {
      if (!this.pointerActive) return;
      const dx = e.clientX - this.dragStartX;
      if (!this.dragging) {
        // Only commit to a drag once movement exceeds a small threshold.
        if (Math.abs(dx) < 8) return;
        this.dragging = true;
        this.track.classList.add('is-dragging');
        try { this.track.setPointerCapture(this.activePointerId); } catch (_) {}
      }
      this.dragDx = dx;
      const slideW = this.track.getBoundingClientRect().width;
      const offset = (this.index * 100) - (this.dragDx / slideW * 100);
      this.track.style.transform = `translate3d(${-offset}%, 0, 0)`;
    }

    onPointerUp(e) {
      if (!this.pointerActive) return;
      this.pointerActive = false;
      // Pure tap (never crossed the drag threshold): let the native click /
      // link navigation happen — don't interfere.
      if (!this.dragging) { this.dragDx = 0; return; }

      this.dragging = false;
      this.track.classList.remove('is-dragging');
      try { this.track.releasePointerCapture(e.pointerId); } catch (_) {}

      const slideW = this.track.getBoundingClientRect().width;
      const threshold = Math.min(80, slideW * 0.15);

      if (this.dragDx > threshold) {
        this.go(this.index - 1);
      } else if (this.dragDx < -threshold) {
        this.go(this.index + 1);
      } else {
        this.activate(this.index);
      }
      this.dragDx = 0;
    }

    go(target) {
      if (this.autoplay) this.resumeAutoplay(); // resume after manual nav / swipe (no-op while hovered)
      let next = target;
      if (this.loop) {
        next = ((target % this.count) + this.count) % this.count;
      } else {
        next = Math.max(0, Math.min(this.count - 1, target));
      }
      if (next === this.index && this.dragDx === 0) {
        this.activate(this.index);
        return;
      }
      this.activate(next);
    }

    activate(i, opts = {}) {
      const prev = this.index;
      this.index = i;

      if (opts.skipAnimation) {
        this.track.style.transition = 'none';
        requestAnimationFrame(() => {
          this.track.style.transition = '';
        });
      }
      this.track.style.transform = `translate3d(${-i * 100}%, 0, 0)`;

      this.slides.forEach((slide, idx) => {
        slide.classList.toggle('is-active', idx === i);
      });

      this.dots.forEach((dot, idx) => {
        const active = idx === i;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      // Reset the progress pager for the new slide
      if (this.autoplay) {
        this.elapsed = 0;
        this.setProgress(0);
      } else if (typeof this.setProgress === 'function') {
        this.setProgress(1); // not auto-advancing → active pill stays solid
      }

      this.pauseAllVideosExcept(i);
      this.activateSlideMedia(i);
      if (prev !== i) this.deactivateSlideMedia(prev);
      this.applySound();
      this.syncMediaControls();
    }

    /* ---------- Optional play/pause + sound controls ---------- */

    // Pause holds BOTH the active video and the slide auto-advance, so one control
    // stops every moving thing in the section (WCAG 2.2.2 "Pause, Stop, Hide").
    togglePlayPause() {
      this.userPaused = !this.userPaused;
      if (this.userPaused) {
        this.pauseAutoplay();
        this.deactivateSlideMedia(this.index);
      } else {
        // Pressing Play is an explicit request to resume — clear the hover/focus holds
        // that the click itself just put in place.
        this.hovered = false;
        this.focused = false;
        this.resumeAutoplay();
        this.activateSlideMedia(this.index);
        this.applySound();
      }
      this.syncPlayPauseButton();

      // A swapped aria-label isn't reliably re-announced, so also speak the resulting state —
      // here and not in syncPlayPauseButton(), which also runs on every slide change.
      const status = this.statusRegion;
      if (status) {
        status.textContent = this.userPaused ? status.dataset.textPaused : status.dataset.textPlaying;
      }
    }

    toggleSound() {
      this.soundOn = !this.soundOn;
      this.applySound();
      this.syncMuteButton();
    }

    // Only the ACTIVE, currently visible video carries sound; everything else stays muted
    // (a slide can hold a desktop + a mobile video, and only one of them is displayed).
    applySound() {
      this.slides.forEach((slide, idx) => {
        const live = this.soundOn && idx === this.index;
        slide.querySelectorAll('[data-carousel-video]').forEach((v) => {
          v.muted = !(live && this.isVisible(v));
        });
        slide.querySelectorAll('[data-carousel-iframe]').forEach((iframe) => {
          this.setEmbedMuted(iframe, !(live && this.isVisible(iframe)));
        });
      });
    }

    setEmbedMuted(iframe, muted) {
      const provider = iframe.dataset.provider;
      if (!iframe.contentWindow) return;
      try {
        if (provider === 'youtube') {
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: muted ? 'mute' : 'unMute', args: [] }),
            '*'
          );
        } else if (provider === 'vimeo') {
          iframe.contentWindow.postMessage(
            JSON.stringify({ method: 'setVolume', value: muted ? 0 : 1 }),
            '*'
          );
        }
      } catch (_) {}
    }

    isVisible(el) {
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    }

    // Sound is meaningless without a video; play/pause still is if the slides auto-advance.
    syncMediaControls() {
      if (!this.mediaControls) return;
      const slide = this.slides[this.index];
      const media = slide ? Array.from(slide.querySelectorAll('[data-carousel-video], [data-carousel-iframe]')) : [];
      const hasVideo = media.some((el) => this.isVisible(el));

      if (this.muteBtn) this.muteBtn.hidden = !hasVideo;
      if (this.playPauseBtn) this.playPauseBtn.hidden = !hasVideo && !this.autoplay;
      this.mediaControls.hidden =
        (!this.playPauseBtn || this.playPauseBtn.hidden) && (!this.muteBtn || this.muteBtn.hidden);

      this.syncPlayPauseButton();
      this.syncMuteButton();
      this.positionMediaControls();
    }

    // Keep the controls inside `.hero-carousel__media`. They are absolutely positioned
    // against `.hero-carousel`, which on mobile is TALLER than the media (text-below-media
    // mode) — and the media's own height varies per slide (mobile "Fit" uses the image's
    // ratio, not the fixed Mobile height). So measure the active slide's media and publish
    // its bottom edge; the CSS pins to that instead of guessing from --section-height.
    positionMediaControls() {
      if (!this.mediaControls) return;
      const slide = this.slides[this.index];
      const media = slide && slide.querySelector('.hero-carousel__media');
      if (!media) return;
      const bottom = media.getBoundingClientRect().bottom - this.root.getBoundingClientRect().top;
      if (bottom > 0) this.root.style.setProperty('--media-bottom', Math.round(bottom) + 'px');
    }

    syncPlayPauseButton() {
      if (!this.playPauseBtn) return;
      const playing = !this.userPaused;
      // Theme convention (section-shoppable-video.css): the modifier names the STATE,
      // and --play is what HIDES the play icon. Same classes video-controls.js uses.
      this.playPauseBtn.classList.toggle('shoppable-video__control-button--play', playing);
      this.playPauseBtn.classList.toggle('shoppable-video__control-button--pause', !playing);
      const labels = this.mediaControls ? this.mediaControls.dataset : {};
      const label = playing ? labels.labelPause : labels.labelPlay;
      if (label) this.playPauseBtn.setAttribute('aria-label', label);
    }

    syncMuteButton() {
      if (!this.muteBtn) return;
      // .wt-hero-video__sound-toggle shows the muted icon by default; --unmuted swaps it
      this.muteBtn.classList.toggle('wt-hero-video__sound-toggle--unmuted', this.soundOn);
      this.muteBtn.setAttribute('data-sound', this.soundOn ? 'on' : 'off');
      this.muteBtn.setAttribute('aria-pressed', this.soundOn ? 'true' : 'false');
    }

    pauseAllVideosExcept(activeIndex) {
      this.slides.forEach((slide, idx) => {
        if (idx === activeIndex) return;

        // Native videos
        slide.querySelectorAll('[data-carousel-video]').forEach((v) => {
          try { v.pause(); v.currentTime = 0; } catch (_) {}
        });

        // YouTube / Vimeo iframes — postMessage pause
        slide.querySelectorAll('[data-carousel-iframe]').forEach((iframe) => {
          this.pauseEmbed(iframe);
        });
      });
    }

    pauseEmbed(iframe) {
      const provider = iframe.dataset.provider;
      if (!iframe.contentWindow) return;
      try {
        if (provider === 'youtube') {
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
            '*'
          );
        } else if (provider === 'vimeo') {
          iframe.contentWindow.postMessage(
            JSON.stringify({ method: 'pause' }),
            '*'
          );
        }
      } catch (_) {}
    }

    activateSlideMedia(i) {
      const slide = this.slides[i];
      if (!slide) return;
      if (this.userPaused) return; // visitor pressed Pause — don't restart the media behind their back

      // Lazy-load iframe src on first activation
      slide.querySelectorAll('[data-carousel-iframe]').forEach((iframe) => {
        if (!iframe.getAttribute('src') && iframe.dataset.embedBase) {
          const base = iframe.dataset.embedBase;
          const sep = base.includes('?') ? '&' : '?';
          iframe.setAttribute('src', `${base}${sep}autoplay=1`);
        } else {
          this.playEmbed(iframe);
        }
      });

      // Autoplay native videos on the active slide
      slide.querySelectorAll('[data-carousel-video]').forEach((v) => {
        v.preload = 'auto';
        const playPromise = v.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      });
    }

    deactivateSlideMedia(i) {
      const slide = this.slides[i];
      if (!slide) return;
      slide.querySelectorAll('[data-carousel-video]').forEach((v) => {
        try { v.pause(); } catch (_) {}
      });
      slide.querySelectorAll('[data-carousel-iframe]').forEach((iframe) => {
        this.pauseEmbed(iframe);
      });
    }

    playEmbed(iframe) {
      const provider = iframe.dataset.provider;
      if (!iframe.contentWindow) return;
      try {
        if (provider === 'youtube') {
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
            '*'
          );
        } else if (provider === 'vimeo') {
          iframe.contentWindow.postMessage(
            JSON.stringify({ method: 'play' }),
            '*'
          );
        }
      } catch (_) {}
    }
  }

  function init() {
    document.querySelectorAll('[data-lp-hero-carousel]').forEach((el) => {
      if (el.__heroCarouselReady) return;
      el.__heroCarouselReady = true;
      new HeroCarousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify theme editor section load
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', (e) => {
    // Works whether the selected block is a slide or a content block inside a slide.
    const slide = e.target.closest('[data-carousel-slide]');
    if (!slide) return;
    const root = slide.closest('[data-lp-hero-carousel]');
    if (!root) return;
    let inst = root.__heroCarouselInstance;
    if (!inst) { inst = new HeroCarousel(root); root.__heroCarouselInstance = inst; }
    // Derive index from DOM order (theme blocks have no forloop index).
    const idx = inst.slides.indexOf(slide);
    if (idx >= 0) inst.go(idx);
  });
})();